import { Link } from "react-router-dom";
import { ArrowRight, DollarSign, Eye, Plus, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useEffect, useState } from "react";
import { customerService } from "@/lib/database";

interface TopCustomer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  totalSpent: number;
  totalShipments: number;
}

export function TopCustomers() {
  const [topCustomers, setTopCustomers] = useState<TopCustomer[]>([]);
  const [_, setLoadingCustomers] = useState(true);

  useEffect(() => {
    loadTopCustomers();
  }, []);

  const loadTopCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const topCustomerData = await customerService.getTopCustomersWithStats(5);
      setTopCustomers(topCustomerData || []);
    } catch (error) {
      console.error("Error loading top customers:", error);
      setTopCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between space-x-2">
          <div>
            <CardTitle className="text-xl md:text-2xl">Top Customers</CardTitle>
            <CardDescription>Top 5 customers by total spending</CardDescription>
          </div>

          <div className="flex space-y-2 md:space-y-0 flex-col md:flex-row md:space-x-2 items-center justify-center">
            <Link to="/admin/customers" className="w-full">
              <Button variant="outline" className="w-full">
                All Customers
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>

            <Link to="/admin/customers/new">
              <Button className="bg-[#254164] hover:bg-[#1a2d47] text-white">
                <Plus className="h-4 w-4 mr-2" />
                New Customer
              </Button>
            </Link>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {topCustomers && topCustomers.length > 0 ? (
            topCustomers.map((customer) => (
              <div
                key={customer.id}
                className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white shadow-sm transition-colors"
              >
                <div className="flex items-center space-x-3 mb-3 md:mb-0">
                  <div className="h-10 w-10 rounded-full bg-[#254164] text-white flex items-center justify-center font-semibold">
                    {customer.first_name[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {customer.first_name} {customer.last_name}
                    </p>
                    <p className="text-sm text-gray-600">{customer.email}</p>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-center md:space-x-6 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <span>{customer.totalShipments} shipments</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-gray-900">
                      ${customer.totalSpent}
                    </span>
                  </div>

                  <div className="mt-2 md:mt-0">
                    <Link to={`/admin/customers/${customer.id}`}>
                      <Button size="sm" className="mt-4 md:mt-0">
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No customer data available</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
